/// <amd-module name="Repos/Views/PullRequest/Markdown/CodeSuggestion" />
import * as React from "react";
import { Diff } from "Repos/Changes/Components/Diff/Diff";
import { ILocalFileChangesCollection, ILocalFileChangesEvent } from "Repos/Changes/Contracts";
import { ChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { getGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { FileDiffBlockChangeType, IChange, IFileCharDiffBlock, IFileDiff, IFileDiffBlock, IFileDiffParameters } from "Repos/ChangesCommon/Contracts";
import {
    CommentPosition,
    GitPullRequestCommentThread,
    GitVersionDescriptor,
    PullRequestStatus,
    VersionControlChangeType
} from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { getVersionString } from "Repos/Common/Util/Version";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { IObservableValue, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IMarkdownRendererTransformer, IMarkdownTransformContext } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { IMarkdownToolbarCommand } from "VSS/Features/MarkdownEditor/Commands/Toolbar";
import { wrapWithSign } from "VSS/Features/MarkdownEditor/Formatting/Common";
import { IMarkdownFormattingArgs, IMarkdownFormattingResult } from "VSS/Features/MarkdownEditor/Formatting/Contracts";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { Button } from "VSSUI/Button";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Observer } from "VSSUI/Observer";
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { Tooltip } from "VSSUI/TooltipEx";
import { IGitPullRequestCommentThreadModel } from "../Discussion/PullRequestDiscussionThreadService";
import { hasPermission, PullRequestPermissions } from "../PullRequestPermissionService";
import { getPullRequestViewService } from "../PullRequestViewService";
import { getDiscussionThreadFileDiff, getFileDiffParameters } from "../Util/Diff";

interface IOriginalPosition {
    start: CommentPosition;
    end: CommentPosition;
    iterationId: number;
}

function getOriginalPosition(threadModel: IGitPullRequestCommentThreadModel): IOriginalPosition {
    const prContext = threadModel.thread.pullRequestThreadContext;
    let start = threadModel.thread.threadContext.rightFileStart;
    let end = threadModel.thread.threadContext.rightFileEnd;
    let iterationId = -1;

    if (prContext.iterationContext) {
        iterationId = prContext.iterationContext.secondComparingIteration || -1;
    }
    if (prContext.trackingCriteria) {
        start = prContext.trackingCriteria.origRightFileStart || start;
        end = prContext.trackingCriteria.origRightFileEnd || end;
    }

    return { start, end, iterationId };
}

function getSupportedChange(pageContext: IVssPageContext, thread: GitPullRequestCommentThread): IChange | undefined {
    // "Suggestions" are supported on comments added to the right side of non-deleted files, only at the line level (not at the file level)
    if (
        thread.threadContext &&
        thread.threadContext.filePath &&
        thread.threadContext.rightFileStart &&
        thread.threadContext.rightFileStart.line > 0
    ) {
        const change = getPullRequestViewService(pageContext).getChanges().value.changesByPath[thread.threadContext.filePath];
        if (change && (change.changeType & VersionControlChangeType.Delete) === 0) {
            return change;
        }
    }
}

function getContentFromDiff(position: IOriginalPosition, diff?: IFileDiff): string {
    const content: string[] = [];
    if (diff && diff.blocks) {
        for (const block of diff.blocks) {
            if (block.mLine <= position.end.line && block.mLine + block.mLinesCount - 1 >= position.start.line) {
                for (
                    let i = Math.max(position.start.line, block.mLine), end = Math.min(position.end.line, block.mLine + block.mLinesCount - 1);
                    i <= end;
                    i++
                ) {
                    let line = block.mLines[i - block.mLine];

                    // If the start and end positions are the exact same, then consider this a full-line selection.
                    if (position.start.offset !== position.end.offset || position.start.line !== position.end.line) {
                        if (i === position.end.line && position.end.offset > 0) {
                            line = line.substr(0, position.end.offset - 1);
                        }
                        if (i === position.start.line && position.start.offset > 1) {
                            line = line.substr(position.start.offset - 1);
                        }
                    }

                    content.push(line);
                }
            }
        }
    }
    return content.join("\n");
}

function getDiffForSuggestion(
    fileDiff: IReadonlyObservableValue<IFileDiff | undefined> | undefined,
    contentDiff: IReadonlyObservableValue<IFileDiff | undefined> | undefined,
    suggestionText: string | null,
    startPosition: CommentPosition,
    endPosition: CommentPosition,
    fetchContentDiff: (originalContent: string, modifiedContent: string, startLineNumber: number) => IFileDiff | undefined
): IFileDiff | undefined {
    let result: IFileDiff | undefined;
    let contentDiffValue = contentDiff && contentDiff.value;

    if (fileDiff && fileDiff.value) {
        // We have the file diff from the PR. Use it to get the content being modified. Use the "modified" content
        // in the PR diff as the original side being modified by this suggestion
        const diff = fileDiff.value;
        let firstLinePrefix = "";
        let lastLinePostfix = "";
        const oldLines: string[] = [];
        if (diff.blocks) {
            for (const block of diff.blocks) {
                if (block.mLine <= endPosition.line && block.mLine + block.mLinesCount - 1 >= startPosition.line) {
                    for (
                        let i = Math.max(startPosition.line, block.mLine), end = Math.min(endPosition.line, block.mLine + block.mLinesCount - 1);
                        i <= end;
                        i++
                    ) {
                        oldLines.push(block.mLines[i - block.mLine]);
                        if (i === startPosition.line && startPosition.offset > 1) {
                            firstLinePrefix = block.mLines[i - block.mLine].substr(0, startPosition.offset - 1);
                        }
                        if (i === endPosition.line && endPosition.offset > 0) {
                            lastLinePostfix = block.mLines[i - block.mLine].substr(endPosition.offset - 1);
                        }
                    }
                }
            }
        }

        // The modified side of the diff is the suggestion text left in this comment
        let newLines: string[];
        if (suggestionText === null) {
            newLines = [];
            if (firstLinePrefix || lastLinePostfix) {
                newLines.push(firstLinePrefix + lastLinePostfix);
            }
        } else {
            newLines = suggestionText.split("\n");
            newLines[0] = firstLinePrefix + newLines[0];
            newLines[newLines.length - 1] += lastLinePostfix;
        }

        const diffBlock = {
            changeType: FileDiffBlockChangeType.Edit,
            mLine: startPosition.line,
            mLines: newLines,
            mLinesCount: newLines.length,
            oLine: startPosition.line,
            oLines: oldLines,
            oLinesCount: oldLines.length
        } as IFileDiffBlock;

        result = {
            blocks: [diffBlock],
            lineCharBlocks: [{ charChange: [], lineChange: diffBlock } as IFileCharDiffBlock],
            modifiedFile: diff.modifiedFile,
            originalFile: diff.originalFile
        } as IFileDiff;

        // Ensure that we've kicked off fetching the detailed content diff
        if (!contentDiffValue) {
            let newContent = newLines.join("\n");
            if (!newContent && newLines.length > 0) {
                newContent = " ";
            }
            contentDiffValue = fetchContentDiff(oldLines.join("\n"), newContent, startPosition.line);
        }
    }

    // Use the detailed diff blocks unless the changes are identical except for leading
    // and trailing whitespace. The server diff will claim these files are "identical"
    // so we can just keep using our basic before/after content diff with no block highlighting
    if (contentDiffValue && !contentDiffValue.identicalContent) {
        result = { ...contentDiffValue };
        result.lineCharBlocks = [...result.lineCharBlocks];

        const lineDelta = startPosition.line - 1;
        for (let i = 0, l = result.lineCharBlocks.length; i < l; i++) {
            const block = result.lineCharBlocks[i];
            result.lineCharBlocks[i] = {
                ...block,
                lineChange: { ...block.lineChange, mLine: block.lineChange.mLine + lineDelta, oLine: block.lineChange.oLine + lineDelta }
            };
        }

        result.blocks = result.lineCharBlocks.map(b => b.lineChange);
    }

    return result;
}

const suggestionRegex = new RegExp("```suggestion\\r?\\n([\\S\\s]*?)\\r?\\n```", "g");
const emptySuggestionRegex = new RegExp("```suggestion\\r?\\n```", "g");

export function getMarkdownTransformers(
    pageContext: IVssPageContext,
    threadModel?: IGitPullRequestCommentThreadModel
): IMarkdownRendererTransformer[] {
    const transformers: IMarkdownRendererTransformer[] = [];
    if (threadModel) {
        const change = getSupportedChange(pageContext, threadModel.thread);
        if (change) {
            transformers.push((context: IMarkdownTransformContext): void => {
                context.markup = context.markup.replace(emptySuggestionRegex, () => {
                    return context.createReplacement(
                        <PullRequestCodeSuggestion change={change} pageContext={pageContext} suggestionText={null} threadModel={threadModel} />
                    );
                });
                context.markup = context.markup.replace(suggestionRegex, (fullMatch: string, suggestionText: string) => {
                    return context.createReplacement(
                        <PullRequestCodeSuggestion
                            change={change}
                            pageContext={pageContext}
                            suggestionText={suggestionText}
                            threadModel={threadModel}
                        />
                    );
                });
            });
        }
    }

    return transformers;
}

interface IPullRequestCodeSuggestionProps {
    change: IChange;
    pageContext: IVssPageContext;
    suggestionText: string | null;
    threadModel: IGitPullRequestCommentThreadModel;
}

const PullRequestCodeSuggestion = (props: IPullRequestCodeSuggestionProps) => {
    const { change, pageContext, suggestionText, threadModel } = props;
    const filePath = threadModel.thread.threadContext.filePath;
    const originalPath = threadModel.thread.pullRequestThreadContext?.trackingCriteria?.origFilePath || filePath;

    const prViewService = getPullRequestViewService(pageContext);
    const gitFilesService = pageContext.getService<IGitFilesService>("IGitFilesService");
    const navigationService = getGitNavigationService(pageContext);

    const position = getOriginalPosition(threadModel);
    const { modifiedVersion } = prViewService.getVersionsForIterationRange(position.iterationId, -1);
    const [fileDiff, setFileDiff] = React.useState<IReadonlyObservableValue<IFileDiff | undefined>>();
    React.useEffect(() => {
        getDiscussionThreadFileDiff(pageContext, threadModel.thread, change, true).then(observable => setFileDiff(observable));
    }, []);

    const [contentDiff, setContentDiff] = React.useState<IReadonlyObservableValue<IFileDiff | undefined>>();

    const fetchContentDiff = (originalContent: string, modifiedContent: string, startLineNumber: number): IFileDiff | undefined => {
        // Don't fetch the detailed content diff while the comment/suggestion is being edited. This would
        // result in a lot of expensive round trips and computations as the user edits the comment.
        // While editing, a simple preview will be shown with before/after content but no inner-line diffs
        if (!contentDiff && !threadModel.isDirty().value) {
            const newContentDiff = gitFilesService.getFileDiff({
                includeCharDiffs: true,
                modifiedContent,
                modifiedPath: originalPath,
                modifiedVersion: getVersionString(modifiedVersion),
                originalContent,
                originalPath: originalPath,
                originalVersion: getVersionString(modifiedVersion),
                partialDiff: false
            });
            setContentDiff(newContentDiff);
            return newContentDiff.value;
        }
    };

    const replacement: ICodeReplacement = {
        endPosition: position.end,
        originalPath,
        filePath,
        replacementValue: suggestionText,
        startPosition: position.start,
        thread: threadModel.thread,
        version: modifiedVersion
    };

    const suggestedChangeService = getCodeSuggestionService(pageContext);
    const applyFailure = new ObservableValue(false);

    return (
        <ChangesContext.Provider value={{ filesService: gitFilesService, navigationService }}>
            <div className="flex-column margin-top-16 rhythm-vertical-8 subtle-border padding-8 margin-bottom-16 h-scroll-auto">
                <Observer
                    appliedReplacements={suggestedChangeService.getReplacementsForFile(filePath)}
                    applyFailure={applyFailure}
                    canApply={suggestedChangeService.canApplyReplacement(replacement)}
                    fileIsDirty={prViewService.getLocalChanges().getFile(filePath).isDirty()}
                    isCommentDirty={threadModel.isDirty()}
                    status={prViewService.getStatus()}
                >
                    {(props: {
                        applyFailure: boolean;
                        canApply: boolean;
                        fileIsDirty: boolean;
                        isCommentDirty: boolean;
                        status: PullRequestStatus;
                    }) => {
                        let actions: JSX.Element | null = null;
                        let pills: JSX.Element | null = null;

                        if (!props.isCommentDirty && props.canApply) {
                            if (suggestedChangeService.isReplacementApplied(replacement)) {
                                if (props.fileIsDirty) {
                                    actions = (
                                        <>
                                            <Button
                                                onClick={async () => {
                                                    const result = await suggestedChangeService.tryUndoReplacement(replacement);
                                                    if (!result) {
                                                        applyFailure.value = true;
                                                    }
                                                }}
                                                text={Resources.UndoChange}
                                            />
                                            <Button
                                                onClick={() => prViewService.commitLocalChanges()}
                                                primary={true}
                                                text={Resources.CommitChanges}
                                            />
                                        </>
                                    );
                                    pills = (
                                        <Pill
                                            className="pr-pending-suggestion"
                                            excludeFocusZone={true}
                                            excludeTabStop={true}
                                            size={PillSize.compact}
                                            variant={PillVariant.outlined}
                                        >
                                            <Tooltip text={Resources.PendingSuggestedChangeTooltip}>
                                                <span>{Resources.PendingSuggestedChange}</span>
                                            </Tooltip>
                                        </Pill>
                                    );
                                } else {
                                    pills = (
                                        <Pill
                                            className="pr-committed-suggestion"
                                            excludeFocusZone={true}
                                            excludeTabStop={true}
                                            size={PillSize.compact}
                                            variant={PillVariant.outlined}
                                        >
                                            {Resources.CommittedSuggestedChange}
                                        </Pill>
                                    );
                                }
                            } else if (
                                hasPermission(pageContext, PullRequestPermissions.PushToSourceBranch) &&
                                props.status === PullRequestStatus.Active
                            ) {
                                actions = (
                                    <Button
                                        onClick={async () => {
                                            const result = await suggestedChangeService.tryApplyReplacement(replacement);
                                            if (!result) {
                                                applyFailure.value = true;
                                            }
                                        }}
                                        text={Resources.ApplyChange}
                                    />
                                );
                            }
                        }

                        return (
                            <>
                                <div className="flex-row flex-center rhythm-horizontal-8">
                                    <span className="font-weight-semibold text-ellipsis margin-left-8">{Resources.SuggestedChange}</span>
                                    {pills}
                                    <div className="flex-grow"></div>
                                    {actions}
                                </div>

                                {props.applyFailure ? (
                                    <MessageBar onDismiss={() => (applyFailure.value = false)} severity={MessageBarSeverity.Error}>
                                        {Resources.CannotApplyChangeMessage}
                                    </MessageBar>
                                ) : null}
                            </>
                        );
                    }}
                </Observer>
                <div className="flex-column">
                    <Observer contentDiff={contentDiff} diff={fileDiff}>
                        {() => {
                            return fileDiff &&
                                fileDiff.value &&
                                !(
                                    (fileDiff.value.modifiedFileTruncated &&
                                        fileDiff.value.blocks[0].mLinesCount >= replacement.startPosition.line &&
                                        fileDiff.value.blocks[0].mLinesCount >= replacement.endPosition.line) ||
                                    !fileDiff.value.modifiedFileTruncated
                                ) ? (
                                <MessageBar severity={MessageBarSeverity.Warning}>{Resources.TooLarge}</MessageBar>
                            ) : (
                                <Diff
                                    diff={getDiffForSuggestion(fileDiff, contentDiff, suggestionText, position.start, position.end, fetchContentDiff)}
                                    diffOrientation={DiffViewerOrientation.Inline}
                                />
                            );
                        }}
                    </Observer>
                </div>
            </div>
        </ChangesContext.Provider>
    );
};

export function getMarkdownCommands(pageContext: IVssPageContext, threadModel?: IGitPullRequestCommentThreadModel): IMarkdownToolbarCommand[] {
    const toolbarButtons: IMarkdownToolbarCommand[] = [];

    if (threadModel) {
        const change = getSupportedChange(pageContext, threadModel.thread);
        if (change) {
            toolbarButtons.push({
                iconProps: { iconName: "Insights" },
                id: "pr-suggestion",
                formatText: (formattingArgs: IMarkdownFormattingArgs): Promise<IMarkdownFormattingResult> => {
                    const { text = "" } = formattingArgs;
                    let selectionStart = Math.min(formattingArgs.selectionStart || 0, text.length);
                    let selectionEnd = Math.min(formattingArgs.selectionEnd || 0, text.length);
                    const hasSelection = selectionEnd > selectionStart;

                    let startSuggestionIndicator = "```suggestion\n";
                    if (selectionStart > 0 && text[selectionStart - 1] !== "\n") {
                        startSuggestionIndicator = "\n" + startSuggestionIndicator;
                    }

                    if (hasSelection) {
                        return Promise.resolve(wrapWithSign(formattingArgs, startSuggestionIndicator, "\n```\n"));
                    } else {
                        const prViewService = getPullRequestViewService(pageContext);
                        const position = getOriginalPosition(threadModel);
                        const { originalVersion, modifiedVersion } = prViewService.getVersionsForIterationRange(position.iterationId, -1);

                        const diffParameters = getFileDiffParameters(
                            pageContext,
                            change.path,
                            change.sourcePath,
                            change.changeType,
                            originalVersion,
                            modifiedVersion
                        );
                        const gitFilesService = pageContext.getService<IGitFilesService>("IGitFilesService");
                        return gitFilesService.getFileDiffPromise(diffParameters).then(diff => {
                            const existingContent = getContentFromDiff(position, diff);

                            const firstPart = text.substring(0, selectionStart);
                            const middlePart = text.substring(selectionStart, selectionEnd);
                            const endPart = text.substring(selectionEnd);

                            const insertText = startSuggestionIndicator + existingContent + "\n```\n";
                            selectionStart += startSuggestionIndicator.length;
                            selectionEnd += startSuggestionIndicator.length + existingContent.length;

                            return {
                                insertSelectionEnd: firstPart.length + middlePart.length,
                                insertSelectionStart: firstPart.length,
                                insertText,
                                finalSelectionEnd: selectionEnd,
                                finalSelectionStart: selectionStart,
                                finalText: firstPart + insertText + endPart
                            };
                        });
                    }
                },
                groupKey: "code",
                order: 5,
                tooltipProps: { text: Resources.SuggestionTooltip }
            });
        }
    }

    return toolbarButtons;
}

export interface ICodeReplacement {
    committed?: boolean;
    endPosition: CommentPosition;
    originalPath: string;
    filePath: string;
    replacementValue: string | null;
    startPosition: CommentPosition;
    thread: GitPullRequestCommentThread;
    version: GitVersionDescriptor;
}

export interface IPullRequestCodeSuggestionService extends IVssService {
    canApplyReplacement(replacement: ICodeReplacement): IObservableValue<boolean>;
    getReplacementsForFile(path: string): ObservableArray<ICodeReplacement>;
    isReplacementApplied(replacement: ICodeReplacement): boolean;
    tryApplyReplacement(replacement: ICodeReplacement): Promise<boolean>;
    tryUndoReplacement(replacement: ICodeReplacement): Promise<boolean>;
}

interface ICodeReplacementRevert {
    endPosition: CommentPosition;
    modifiedContent: string;
    originalReplacementValue: string | null;
    replacement: ICodeReplacement;
    startPosition: CommentPosition;
}

class PullRequestCodeSuggestionService extends VssService implements IPullRequestCodeSuggestionService {
    private localChanges: ILocalFileChangesCollection;
    private replacementsByFile: { [path: string]: ObservableArray<ICodeReplacement> } = {};
    private reverts: ICodeReplacementRevert[] = [];

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this.localChanges = getPullRequestViewService(this.pageContext).getLocalChanges();
        this.localChanges.subscribe(this.onChangesVersionUpdated, "updateVersion");
        this.localChanges.subscribe(this.onChangesReverted, "revert");
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        super._serviceEnd(pageContext);

        this.localChanges.unsubscribe(this.onChangesVersionUpdated, "updateVersion");
        this.localChanges.unsubscribe(this.onChangesReverted, "revert");
    }

    public getReplacementsForFile(path: string): ObservableArray<ICodeReplacement> {
        let replacements = this.replacementsByFile[path];
        if (!replacements) {
            replacements = new ObservableArray();
            this.replacementsByFile[path] = replacements;
        }

        return replacements;
    }

    public isReplacementApplied(replacement: ICodeReplacement): boolean {
        const replacements = this.getReplacementsForFile(replacement.filePath);
        return replacements.value.some(r => this.replacementsEqual(r, replacement));
    }

    public async tryApplyReplacement(replacement: ICodeReplacement): Promise<boolean> {
        const revert = await this.applyReplacement(
            replacement,
            replacement.filePath,
            replacement.startPosition,
            replacement.endPosition,
            replacement.replacementValue,
            true,
            getVersionString(replacement.version),
            undefined
        );
        if (revert) {
            this.getReplacementsForFile(replacement.filePath).push(replacement);
            this.reverts.push(revert);
        }
        return !!revert;
    }

    public async tryUndoReplacement(replacement: ICodeReplacement): Promise<boolean> {
        const revert = this.reverts.find(r => this.replacementsEqual(replacement, r.replacement));
        if (!revert) {
            return Promise.resolve(false);
        }

        const result = await this.applyReplacement(
            replacement,
            replacement.filePath,
            revert.startPosition,
            revert.endPosition,
            revert.originalReplacementValue,
            false,
            getVersionString(replacement.version),
            revert.modifiedContent
        );
        if (result) {
            // Remove the replacement
            const fileReplacements = this.getReplacementsForFile(replacement.filePath);
            fileReplacements.value = fileReplacements.value.filter(r => !this.replacementsEqual(r, replacement));

            // Remove the revert
            this.reverts = this.reverts.filter(r => r !== revert);
        }
        return !!result;
    }

    public canApplyReplacement(replacement: ICodeReplacement): IObservableValue<boolean> {
        const result = new ObservableValue(false);

        const prViewService = getPullRequestViewService(this.pageContext);
        const sourceCommit = prViewService.getMergeSourceCommit();
        if (sourceCommit) {
            const { filePath, originalPath, startPosition, endPosition } = replacement;

            // Diff the content from when the comment was left to the current contents of the file.
            const gitFilesService = this.pageContext.getService<IGitFilesService>("IGitFilesService");
            gitFilesService
                .getFileDiffPromise({
                    ignoreTrimmedWhitespace: true,
                    lineNumbersOnly: true,
                    modifiedPath: filePath,
                    modifiedVersion: getVersionString(sourceCommit),
                    originalPath: originalPath,
                    originalVersion: getVersionString(replacement.version),
                    partialDiff: false
                } as IFileDiffParameters)
                .then(diff => {
                    if (diff && !diff.identicalContent && !diff.whitespaceChangesOnly && diff.blocks) {
                        // Find the diff block that contains the start line number
                        const block = diff.blocks.find(b => startPosition.line >= b.oLine && startPosition.line <= b.oLine + b.oLinesCount - 1);

                        // If we didn't find the diff block, or it was not an unchanged block,
                        // or the block doesn't include the full replacement selection, then we can't apply this suggestion
                        result.value = Boolean(
                            block && block.changeType === FileDiffBlockChangeType.None && endPosition.line <= block.oLine + block.oLinesCount - 1
                        );
                    } else {
                        // Otherwise, we don't know, but let's assume it can; if it cannot, it will fail with detailed error in the process.
                        result.value = true;
                    }
                });
        }

        return result;
    }

    private async applyReplacement(
        replacement: ICodeReplacement,
        filePath: string,
        startPosition: CommentPosition,
        endPosition: CommentPosition,
        replacementValue: string | null,
        detectLineEndings: boolean,
        originalVersion?: string,
        originalContent?: string
    ): Promise<ICodeReplacementRevert | undefined> {
        try {
            const file = this.localChanges.getFile(filePath);

            // Ensure that we've waited to loaded the file's original content, then get its current value
            await file.getOriginalItemContent();
            const fileCurrentContent = file.getText().value!;

            // Diff the content from when the comment was left to the current contents of the file.
            // Look for the original line in the diff -- if it is an "unchanged" block, then we can apply
            // the suggestion at the corresponding "modified" line number. If the line has been changed,
            // then we can't apply the suggestion.
            const gitFilesService = this.pageContext.getService<IGitFilesService>("IGitFilesService");
            const diff = await gitFilesService.getFileDiffPromise({
                ignoreTrimmedWhitespace: true,
                lineNumbersOnly: true,
                modifiedContent: fileCurrentContent,
                modifiedPath: filePath,
                modifiedVersion: originalVersion,
                originalContent,
                originalPath: filePath,
                originalVersion,
                partialDiff: false
            } as IFileDiffParameters);

            if (diff && !diff.identicalContent && !diff.whitespaceChangesOnly && diff.blocks) {
                // Find the diff block that contains the start line number
                const block = diff.blocks.find(b => startPosition.line >= b.oLine && startPosition.line <= b.oLine + b.oLinesCount - 1);
                if (!block || block.changeType !== FileDiffBlockChangeType.None || endPosition.line > block.oLine + block.oLinesCount - 1) {
                    // If we didn't find the diff block, or it was not an unchanged block, or the block doesn't include the full replacement selection
                    // then return undefined to indicate that we can't apply this suggestion
                    return undefined;
                }

                // Adjust the start/end positions based on the diff
                const lineOffset = block.mLine - block.oLine;
                startPosition = { line: startPosition.line + lineOffset, offset: startPosition.offset };
                endPosition = { line: endPosition.line + lineOffset, offset: endPosition.offset };
            }

            // Split the file on new lines (note each line may now end with \r for Windows-style line endings)
            const contentLines = fileCurrentContent.split("\n");

            // Determine if we should use Windows-style line endings based on the line ending used for the
            // first replaced line (or first line in the file if we're at the very end of the file).
            if (detectLineEndings) {
                const detectEndingsLineNumber = startPosition.line === contentLines.length ? 1 : startPosition.line;
                const detectEndingsLine = contentLines[detectEndingsLineNumber] || "";
                if (replacementValue && detectEndingsLine[detectEndingsLine.length - 1] === "\r") {
                    replacementValue = replacementValue.replace(/\n/g, "\r\n");
                }
            }

            // Build up the new content. Start with the lines before the start line
            const newContent = contentLines.slice(0, startPosition.line - 1);

            // On the selection start line, add any content before the selection (column) start and content after the selection (column) end of the selection end line
            let replacementContent = replacementValue || "";
            if (startPosition.offset > 0) {
                replacementContent = contentLines[startPosition.line - 1].substr(0, startPosition.offset - 1) + replacementContent;
            }
            if (endPosition.offset > 0) {
                replacementContent += contentLines[endPosition.line - 1].substr(endPosition.offset - 1);
            }

            // Add the new content line unless we are deleting this whole line
            let removedNewLine = "";
            if (replacementValue !== null || (replacementContent && replacementContent !== "\r")) {
                newContent.push(replacementContent);
            } else {
                removedNewLine = replacementContent + "\n";
            }

            // Add all lines after the selection end
            newContent.push(...contentLines.slice(endPosition.line));

            // Create our replacement result
            const newContentText = newContent.join("\n");

            // Finally, set the new content to our resulting text
            file.setNewContent(newContentText);

            // Adjust based on length of replacement vs old replaced content
            const replacementValueLines = (replacementValue || "").split("\n");
            const newEndPosition = { ...startPosition };
            if (replacementValueLines.length > 1) {
                newEndPosition.line += replacementValueLines.length - 1;
                newEndPosition.offset = replacementValueLines[replacementValueLines.length - 1].length + 1;
            } else {
                newEndPosition.offset += (replacementValue || "").length;
            }

            // Compute the value that we replaced
            const originalReplacementLines: string[] = [];
            if (startPosition.line === endPosition.line) {
                originalReplacementLines.push(contentLines[startPosition.line - 1].substring(startPosition.offset - 1, endPosition.offset - 1));
            } else {
                originalReplacementLines.push(contentLines[startPosition.line - 1].substr(startPosition.offset - 1));
                originalReplacementLines.push(...contentLines.slice(startPosition.line, endPosition.line - 1));
                originalReplacementLines.push(contentLines[endPosition.line - 1].substr(0, endPosition.offset - 1));
            }
            const originalReplacementValue = originalReplacementLines.join("\n") + removedNewLine;

            return {
                endPosition: newEndPosition,
                modifiedContent: newContentText,
                originalReplacementValue,
                replacement,
                startPosition: startPosition
            };
        } catch {
            // Could not fetch the contents/diff. Return undefined to indicate that the diff cannot be applied.
            return undefined;
        }
    }

    private onChangesReverted = (event: ILocalFileChangesEvent): void => {
        for (const path in this.replacementsByFile) {
            if (event.previousDirtyPaths.has(path)) {
                this.replacementsByFile[path].value = [];
            }
        }
    };

    private onChangesVersionUpdated = (event: ILocalFileChangesEvent): void => {
        for (const path in this.replacementsByFile) {
            if (event.previousDirtyPaths.has(path)) {
                for (const replacement of this.replacementsByFile[path].value) {
                    replacement.committed = true;
                }
            }
        }
    };

    private replacementsEqual(r1: ICodeReplacement, r2: ICodeReplacement): boolean {
        return r1.filePath === r2.filePath && r1.replacementValue === r2.replacementValue && r1.thread.id === r2.thread.id;
    }
}

const serviceName = "IPullRequestCodeSuggestionService";
Services.add(serviceName, { serviceFactory: PullRequestCodeSuggestionService });

export function getCodeSuggestionService(pageContext: IVssPageContext): IPullRequestCodeSuggestionService {
    return pageContext.getService<IPullRequestCodeSuggestionService>(serviceName);
}
